import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Search, X, SlidersHorizontal, List, Map as MapIcon } from 'lucide-react';
import {
  useCategories,
  useSearch,
  MarketplaceCategory,
} from '../api/use-marketplace';
import { SiteShell, categoryEmoji } from '../components/site-shell';
import { SalonCard, SalonCardSkeleton } from '../components/salon-card';
import { CityCombobox } from '../components/city-combobox';
import { MobileFacetChip } from '../components/mobile-facet-chip';
import { NearMeButton } from '../components/near-me-button';
import { SalonMap } from '../components/salon-map';
import { detectCountryDomain, COUNTRY_DOMAINS } from '../lib/country-domain';

/**
 * /search results page. Reads filters from query string (sole source of
 * truth) so back/forward + deep-linking work. The filter sidebar mutates
 * the URL; everything else reads from it.
 *
 * Supports a List/Map view toggle (`?view=map`): on desktop the map view is
 * a scrollable card list beside a sticky Leaflet (OSM) map; on mobile it's a
 * full-width map. "Near me" geolocation + the Home-visit facet narrow results.
 */
export function SearchPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const category = params.get('category');
  const q = params.get('q');
  const city = params.get('city');
  // Facet toggles live in the URL so deep links and back/forward work.
  // `mobileAvailable=true` narrows results to salons offering home-visit (mobile) bookings.
  const mobileAvailable = params.get('mobileAvailable') === 'true';
  // Audience facet (V92): CSV of MEN/WOMEN/KIDS in the URL, multi-select chips.
  const audienceCsv = params.get('audience');
  const audiences = (audienceCsv ?? '').split(',').filter(Boolean);
  const toggleAudience = (a: string) => {
    const next = audiences.includes(a) ? audiences.filter((x) => x !== a) : [...audiences, a];
    setParam('audience', next.length ? next.join(',') : null);
  };

  const view = params.get('view') === 'map' ? 'map' : 'list';
  const latParam = params.get('lat');
  const lngParam = params.get('lng');
  const userCoords =
    latParam && lngParam ? { lat: Number(latParam), lng: Number(lngParam) } : null;
  const DEFAULT_RADIUS_KM = 25;

  const countryCtx = detectCountryDomain();
  const country = countryCtx.code;
  const cats = useCategories();
  const search = useSearch({
    category: category || null,
    q: q || null,
    city: city || null,
    country,
    mobileAvailable,
    audience: audienceCsv,
    size: 36,
    lat: userCoords?.lat ?? null,
    lng: userCoords?.lng ?? null,
    radius: userCoords ? DEFAULT_RADIUS_KM : null,
  });

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value && value.trim()) next.set(key, value.trim());
    else next.delete(key);
    setParams(next, { replace: false });
  };

  const onLocated = (lat: number, lng: number) => {
    const next = new URLSearchParams(params);
    next.set('lat', String(lat));
    next.set('lng', String(lng));
    next.set('radius', String(DEFAULT_RADIUS_KM));
    next.set('view', 'map');
    setParams(next, { replace: false });
  };
  const clearNearMe = () => {
    const next = new URLSearchParams(params);
    next.delete('lat');
    next.delete('lng');
    next.delete('radius');
    setParams(next, { replace: false });
  };
  const setView = (v: 'list' | 'map') => setParam('view', v === 'map' ? 'map' : null);

  const clearAll = () => setParams(new URLSearchParams(), { replace: false });

  const activeFilters = useMemo(
    () =>
      [
        q && { key: 'q', label: q, clear: () => setParam('q', null) },
        city && { key: 'city', label: city, clear: () => setParam('city', null) },
        category && {
          key: 'category',
          label:
            cats.data?.find((c) => c.key === category)?.displayName ?? category,
          clear: () => setParam('category', null),
        },
        userCoords && { key: 'nearme', label: t('search.nearMeChip'), clear: clearNearMe },
        mobileAvailable && {
          key: 'mobile',
          label: t('search.mobileChip'),
          clear: () => setParam('mobileAvailable', null),
        },
        ...audiences.map((a) => ({
          key: 'aud-' + a,
          label: t('search.facets.audience.' + a.toLowerCase()),
          clear: () => toggleAudience(a),
        })),
      ].filter(Boolean) as { key: string; label: string; clear: () => void }[],
    [q, city, category, cats.data, params], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const total = search.data?.total ?? 0;

  // On a country domain (salunas.pt/.be/.fr/.nl), an empty result set is a
  // "this country has no salons yet" signal — point users back to salunas.com.
  const countryName = countryCtx.code
    ? t(
        'countrySwitcher.' +
          (COUNTRY_DOMAINS.find((d) => d.code === countryCtx.code)?.i18nKey ?? ''),
      )
    : '';

  return (
    <SiteShell>
      {/* Search bar — sticky just below header */}
      <div className="sticky top-16 z-30 border-b border-border/40 bg-background/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <SearchPill
              icon={<Search className="w-4 h-4 text-muted-foreground" />}
              value={q ?? ''}
              placeholder={t('search.servicePlaceholder')}
              onSubmit={(v) => setParam('q', v || null)}
            />
            <CityComboboxFromUrl
              value={city ?? ''}
              onSubmit={(v) => setParam('city', v || null)}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen((o) => !o)}
              className="md:hidden inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border/60 text-sm font-medium hover:border-primary/40 transition-colors"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {t('search.filtersButton')}
            </button>
          </div>

          {/* Facet chip row — non-sidebar toggles (mode-based filters live
              here so they're one tap away on mobile too). */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <MobileFacetChip
              active={mobileAvailable}
              onToggle={() =>
                setParam('mobileAvailable', mobileAvailable ? null : 'true')
              }
            />
            {['MEN', 'WOMEN', 'KIDS'].map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => toggleAudience(a)}
                aria-pressed={audiences.includes(a)}
                className={[
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                  audiences.includes(a)
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-card text-foreground border-border/60 hover:border-foreground/40',
                ].join(' ')}
              >
                {t('search.facets.audience.' + a.toLowerCase())}
              </button>
            ))}
            <NearMeButton onLocated={onLocated} />
            {/* List / Map segmented toggle */}
            <div className="inline-flex items-center rounded-full border border-border/60 bg-card p-0.5">
              <button
                type="button"
                onClick={() => setView('list')}
                aria-pressed={view === 'list'}
                className={[
                  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors',
                  view === 'list' ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
                ].join(' ')}
              >
                <List className="w-3.5 h-3.5" /> {t('search.viewList')}
              </button>
              <button
                type="button"
                onClick={() => setView('map')}
                aria-pressed={view === 'map'}
                className={[
                  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors',
                  view === 'map' ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
                ].join(' ')}
              >
                <MapIcon className="w-3.5 h-3.5" /> {t('search.viewMap')}
              </button>
            </div>
          </div>

          {activeFilters.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {activeFilters.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={f.clear}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-border/60 bg-card text-xs text-foreground hover:border-primary/40 hover:text-primary transition-colors"
                >
                  {f.label}
                  <X className="w-3 h-3" />
                </button>
              ))}
              <button
                type="button"
                onClick={clearAll}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
              >
                {t('search.clearAll')}
              </button>
            </div>
          )}
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-6 py-10 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-[220px,1fr] gap-10">
          {/* Sidebar — categories */}
          <aside className="hidden md:block">
            <CategorySidebar
              categories={cats.data}
              loading={cats.isLoading}
              selected={category}
              onSelect={(key) => setParam('category', key)}
            />
          </aside>

          {/* Results */}
          <div>
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1">
                  {t('search.resultsLabel')}
                </p>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                  {search.isLoading
                    ? t('search.searching')
                    : total === 1
                      ? t('search.oneSalon')
                      : t('search.manySalons', { count: total })}
                </h1>
              </div>
            </div>

            {search.isLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {Array.from({ length: 6 }).map((_, i) => <SalonCardSkeleton key={i} />)}
              </div>
            )}

            {!search.isLoading && search.data && search.data.results.length === 0 && (
              countryCtx.code ? (
                <EmptyCountry country={countryName} />
              ) : (
                <EmptyResults onReset={clearAll} hasFilters={activeFilters.length > 0} />
              )
            )}

            {!search.isLoading && search.data && search.data.results.length > 0 && view === 'map' && (
              <>
                {/* Mobile/tablet: full-width map (cards available via the List toggle). */}
                <div className="lg:hidden h-[70vh] min-h-[420px]">
                  <SalonMap results={search.data.results} userCoords={userCoords} />
                </div>
                {/* Desktop: scrollable card list (left) + sticky map (right). */}
                <div className="hidden lg:grid lg:grid-cols-[minmax(0,1fr),1.1fr] lg:gap-6 lg:items-start">
                  <div className="flex flex-col gap-5">
                    {search.data.results.map((s) => (
                      <SalonCard key={s.key} salon={s} />
                    ))}
                  </div>
                  <div className="sticky top-32 h-[calc(100vh-9rem)]">
                    <SalonMap results={search.data.results} userCoords={userCoords} />
                  </div>
                </div>
              </>
            )}

            {!search.isLoading && search.data && search.data.results.length > 0 && view === 'list' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-5"
              >
                {search.data.results.map((s) => <SalonCard key={s.key} salon={s} />)}
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Mobile filter drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 md:hidden"
          onClick={() => setDrawerOpen(false)}
        >
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="filter-drawer-title"
            className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-3xl bg-background border-t border-border/40 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 id="filter-drawer-title" className="text-xl font-bold">{t('search.filtersHeading')}</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label={t('common.close')}
                className="p-2 -mr-2 rounded-full hover:bg-secondary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <CategorySidebar
              categories={cats.data}
              loading={cats.isLoading}
              selected={category}
              onSelect={(key) => {
                setParam('category', key);
                setDrawerOpen(false);
              }}
            />
          </motion.div>
        </div>
      )}
    </SiteShell>
  );
}

// Combobox adapter: keeps a local draft in sync with the URL-driven
// `value`, commits on submit. Lets the user retype freely after the URL
// changes (clear-all, chip dismiss) without fighting the input.
function CityComboboxFromUrl({
  value,
  onSubmit,
}: {
  value: string;
  onSubmit: (v: string) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  // Sync from URL when external state changes (and we're not editing).
  useEffect(() => {
    if (document.activeElement?.tagName !== 'INPUT') {
      setDraft(value);
    }
  }, [value]);

  return (
    <CityCombobox
      value={draft}
      onChange={setDraft}
      onSubmit={(v) => onSubmit(v)}
      placeholder={t('search.locationPlaceholder')}
      style="pill"
    />
  );
}

// ─── Search pill (controlled-on-blur input) ────────────────────────────

function SearchPill({
  icon,
  value,
  placeholder,
  onSubmit,
}: {
  icon: React.ReactNode;
  value: string;
  placeholder: string;
  onSubmit: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep draft synced with URL-driven value when the user isn't editing.
  // The pill is uncontrolled while focused so typing doesn't fight the URL,
  // but external changes (clear-all, filter chip dismiss) still flow in.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setDraft(value);
    }
  }, [value]);

  return (
    <label className="flex-1 flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-border/60 bg-card focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-primary/20 transition-colors">
      <span className="flex-shrink-0">{icon}</span>
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => onSubmit(draft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit(draft);
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder={placeholder}
        className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
      />
    </label>
  );
}

// ─── Category sidebar ──────────────────────────────────────────────────

function CategorySidebar({
  categories,
  loading,
  selected,
  onSelect,
}: {
  categories: MarketplaceCategory[] | undefined;
  loading: boolean;
  selected: string | null;
  onSelect: (key: string | null) => void;
}) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-10 rounded-lg bg-muted/60 animate-pulse" />
        ))}
      </div>
    );
  }
  if (!categories) return null;

  const tiles: Array<{ key: string | null; displayName: string }> = [
    { key: null, displayName: t('search.allCategories') },
    ...categories.filter((c) => c.key !== 'other'),
  ];

  return (
    <div>
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3 px-3">
        {t('search.category')}
      </p>
      <ul className="space-y-1">
        {tiles.map((tile) => {
          const active = (tile.key ?? null) === (selected ?? null);
          return (
            <li key={tile.key ?? 'all'}>
              <button
                type="button"
                onClick={() => onSelect(tile.key)}
                className={[
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left',
                  active
                    ? 'bg-primary/[0.08] text-primary font-medium'
                    : 'text-foreground hover:bg-secondary',
                ].join(' ')}
              >
                <span className="w-5 inline-block text-center opacity-80">
                  {tile.key ? categoryEmoji(tile.key) : '✦'}
                </span>
                <span className="font-medium">{tile.displayName}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Country-domain empty state: zero salons on salunas.<tld> → point the user
// back to the global salunas.com. The translated string contains the literal
// "salunas.com"; we split on it so that token renders as a real link while the
// rest of the localized sentence stays intact.
function EmptyCountry({ country }: { country: string }) {
  const { t } = useTranslation();
  const message = t('search.emptyCountry', { country });
  const [before, after] = message.split('salunas.com');
  const link = (
    <a
      href="https://salunas.com"
      className="text-primary underline underline-offset-4 hover:text-foreground transition-colors"
    >
      salunas.com
    </a>
  );

  return (
    <div className="rounded-2xl border border-dashed border-border/80 bg-background/50 p-10 md:p-14 text-center">
      <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
        {after === undefined ? (
          message
        ) : (
          <>
            {before}
            {link}
            {after}
          </>
        )}
      </p>
    </div>
  );
}

function EmptyResults({ onReset, hasFilters }: { onReset: () => void; hasFilters: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-dashed border-border/80 bg-background/50 p-10 md:p-14 text-center">
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
        {t('search.noMatches')}
      </p>
      <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
        {t('search.emptyTitle')}
      </h3>
      <p className="text-muted-foreground max-w-md mx-auto leading-relaxed mb-6">
        {hasFilters ? t('search.emptyWithFilters') : t('search.emptyNoFilters')}
      </p>
      {hasFilters && (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
        >
          {t('search.clearFilters')}
        </button>
      )}
    </div>
  );
}
